import { ref } from 'vue';
import SignalRClient from '@/ts/SignalRClient';
import RoomVO from '@/ts/VO/RoomVO';
import KillPigConfigVO from '@/ts/VO/KillPigConfigVO';

type Settled<T> = { ok: true; value: T } | { ok: false; error: unknown };

/** 单个 Hub 调用失败不影响其它调用（避免一个接口异常把整面板状态卡死） */
function safeInvoke<T>(action: string): Promise<Settled<T>> {
  return SignalRClient.Instance.InvokeAsync<T>(action)
    .then((value) => ({ ok: true as const, value }))
    .catch((error) => ({ ok: false as const, error }));
}

/**
 * “全局控制”面板（QuickKillPanel）的数据与保存逻辑。
 *
 * 该面板只在账号详情页（AccountDetail）使用：详情页没有首页那套编辑态
 * （input / selectRooms / 未保存基线），因此这里提供轻量实现：
 * 直接读服务端权威状态（GetKillPigConfig + GetRoomList），保存时按
 * “先 SetKillPig（房间）再 SetKillPigConfig（总配置）”的顺序写回，与首页保存一致。
 */
export function useQuickKill() {
  /** 杀开关：等同于“全部房间 OpenKillPig” */
  const enabled = ref(false);
  /** 参与门槛金额（KillStartAmount，0 = 不限制） */
  const amount = ref(0);
  /** 可用房间数（仅用于展示/提示，不锁死交互） */
  const roomCount = ref(0);
  /** 是否已至少成功读到一次配置（保存时必须） */
  const configReady = ref(false);

  let config: KillPigConfigVO | null = null;
  let rooms: RoomVO[] = [];

  /**
   * 从服务端拉取权威状态。
   * 两个接口分别容错：房间列表失败时仍能显示/修改门槛金额，配置失败时仍能显示房间数。
   * @returns 配置是否成功拉到（保存前必须为 true，避免拿旧快照回写）
   */
  async function load(): Promise<boolean> {
    const [roomsRes, cfgRes] = await Promise.all([
      safeInvoke<{ [key: string]: RoomVO }>('GetRoomList'),
      safeInvoke<KillPigConfigVO>('GetKillPigConfig'),
    ]);

    if (roomsRes.ok && roomsRes.value) {
      rooms = [];
      for (const key in roomsRes.value) {
        const room = roomsRes.value[key];
        if (!room) continue;
        Object.setPrototypeOf(room, RoomVO.prototype);
        rooms.push(room);
      }
      roomCount.value = rooms.length;
      enabled.value = rooms.some((r) => !!r.OpenKillPig);
    } else {
      console.warn('[QuickKill] 读取房间列表失败:', roomsRes.ok ? '结果为空' : roomsRes.error);
    }

    if (cfgRes.ok && cfgRes.value) {
      config = cfgRes.value;
      configReady.value = true;
      amount.value = Number(config.KillStartAmount ?? 0);
      // 房间列表没取到时，用配置里的总开关兜底
      if (!roomsRes.ok) enabled.value = !!config.OpenKillPig;
      return true;
    }

    console.warn('[QuickKill] 读取杀猪配置失败:', cfgRes.ok ? '结果为空' : cfgRes.error);
    return false;
  }

  /**
   * 保存：只写「总控开关」（确实变化时）与「参与门槛金额」。
   *
   * 关键约束（避免覆盖其它配置）：
   * 1. 服务端 SetKillPigConfig 是**整体替换** Global.KillPigConfig，
   *    所以保存前必须重新拉一次最新配置，只改 KillStartAmount 再回写，
   *    不能用打开面板时的旧快照，否则会把别人刚改的字段覆盖回去。
   * 2. 只有开关真的发生变化时才调用 SetKillPig（它会重设所有房间的 OpenKillPig），
   *    否则只改金额也会把“部分房间开启”变成全部开启/全部关闭。
   */
  async function save(payload: { enabled: boolean; amount: number }): Promise<boolean> {
    const fresh = await load();

    // 没拿到最新配置就绝不写：SetKillPigConfig 是整体替换，旧快照会覆盖别人的修改
    if (!fresh || !config) {
      throw new Error('读取杀猪配置失败，请稍后重试');
    }

    const enabledChanged = payload.enabled !== enabled.value;

    if (enabledChanged) {
      if (payload.enabled && rooms.length === 0) {
        throw new Error('暂无可控房间，请先拉取数据');
      }
      const targetRooms = payload.enabled ? rooms.map((r) => r.RoomNumber) : [];
      const killOk = await SignalRClient.Instance.InvokeAsync<boolean>('SetKillPig', targetRooms);
      if (!killOk) {
        try {
          await load();
        } catch {
          /* ignore */
        }
        return false;
      }
    }

    // 只覆盖门槛金额，其它字段保持刚刚拉取到的最新值
    config.KillStartAmount = payload.amount;
    const configOk = await SignalRClient.Instance.InvokeAsync<boolean>('SetKillPigConfig', config);

    if (configOk) {
      // 仅开关变化时才动本地房间状态，避免把服务端的“部分开启”误标成全开
      if (enabledChanged) {
        for (const room of rooms) room.OpenKillPig = payload.enabled;
      }
      enabled.value = payload.enabled;
      amount.value = payload.amount;
      return true;
    }

    // 保存失败：回读服务端真实状态，别让界面停在错误草稿上
    try {
      await load();
    } catch {
      /* 回读失败忽略，界面保持原值 */
    }
    return false;
  }

  return { enabled, amount, roomCount, configReady, load, save };
}
